import React, { useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import SEO from '../../../components/SEO/SEO';
import { seoConfig } from '../../../config/seo';
import {
    ArrowRight, ArrowLeft, GraduationCap
} from 'lucide-react';
import { OptimizedImage } from '@/components/ui/optimized-image';
import { Typography } from '@/components/ui/typography';
import { m } from 'framer-motion';
import { tutorialCategories, tutorials } from './CourseData';
import { Course, TutorialCategory } from '../types';

interface FAQ {
    question: string;
    answer: string;
}

const courseFAQs: FAQ[] = [
    {
        question: "Are these courses suitable for complete beginners?",
        answer: "Yes, our Engineering Paths are designed to take you from the fundamentals to advanced concepts. We recommend starting with HTML or JavaScript if you're entirely new to web development."
    },
    {
        question: "Do I need to pay for these courses?",
        answer: "No, all course content on FileFusion is free to access. Our goal is to provide high-quality engineering education to everyone."
    },
    {
        question: "Are the code examples tested?",
        answer: "Absolutely. Every code snippet in our courses is tested against modern standards (ES2023+ for JavaScript, Python 3.12, etc.) to ensure accuracy and performance."
    },
    {
        question: "How often is the content updated?",
        answer: "We regularly audit and update our courses to reflect the latest industry trends, security best practices, and language features."
    }
];

const CoursesHub: React.FC = () => {
    const { language: urlLanguage } = useParams<{ language: string }>();
    const [searchParams] = useSearchParams();
    const selectedLanguage = urlLanguage || searchParams.get('language');

    useEffect(() => {
        const handleScroll = () => {
            // Logic for scroll if needed
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const filteredTutorials: Course[] = selectedLanguage
        ? tutorials.filter((t: Course) => t.language.toLowerCase() === selectedLanguage.toLowerCase())
        : tutorials;



    const currentCategory: TutorialCategory | undefined = tutorialCategories.find((cat: TutorialCategory) => cat.id.toLowerCase() === selectedLanguage?.toLowerCase());

    // SEO Data
    const seoData = selectedLanguage && currentCategory
        ? {
            title: `Master ${currentCategory.name} - Complete ${currentCategory.name} Course | FileFusion Learning Center`,
            description: `Comprehensive, original ${currentCategory.name} courses. ${currentCategory.description} Master modern ${currentCategory.name} with step-by-step guides and practical examples.`,
            keywords: `${selectedLanguage}, python courses, html guide, javascript async await, c pointers explained, filefusion courses`,
            type: 'course'
        }
        : seoConfig.courses;

    return (
        <>
            <SEO {...seoData} faqData={courseFAQs} />

            <div className="max-w-full mx-auto p-0 font-brand text-[#202124] bg-white min-h-screen">
                {/* Hero Section */}
                <header className="max-w-7xl mx-auto px-6 pt-16 pb-20 text-center">
                    <Typography variant="hero-title" as="h1" weight="light" className="mb-6 text-[#121317]">
                        {selectedLanguage && currentCategory
                            ? `${currentCategory.name} Engineering Path`
                            : <span>Master engineering. <span className="text-[#1a73e8]">One course at a time.</span></span>}
                    </Typography>
                    <Typography variant="hero-subtitle" weight="normal" className="max-w-2xl mx-auto text-[#5f6368] mb-10">
                        {selectedLanguage && currentCategory
                            ? currentCategory.description
                            : 'Structured programming courses for Python, JavaScript, HTML5, and C — progressive paths from zero to production, free forever.'}
                    </Typography>
                    {!selectedLanguage && (
                        <div className="flex flex-wrap gap-4 justify-center">
                            <Link
                                to="/insights/articles"
                                className="inline-flex items-center gap-2 px-7 py-3 bg-[#1a73e8] text-white font-medium rounded-full no-underline hover:bg-[#1557b0] transition-colors shadow-sm"
                            >
                                Read articles <ArrowRight size={17} />
                            </Link>
                            <Link
                                to="/insights"
                                className="inline-flex items-center gap-2 px-7 py-3 bg-white border border-[#dadce0] text-[#3c4043] font-medium rounded-full no-underline hover:bg-[#f8f9fa] transition-colors"
                            >
                                Back to Insights
                            </Link>
                        </div>
                    )}
                </header>

                {/* Stat strip - only show on hub view */}
                {!selectedLanguage && (
                    <section className="max-w-7xl mx-auto px-6 pb-20 border-b border-[#f1f3f4]">
                        <div className="flex justify-center gap-12 sm:gap-24 flex-wrap">
                            {[
                                { value: '4', label: 'Languages' },
                                { value: '100%', label: 'Free Forever' },
                                { value: '0', label: 'Account Needed' },
                                { value: '∞', label: 'Always Updated' },
                            ].map(s => (
                                <div key={s.label} className="flex flex-col items-center text-center">
                                    <div className="text-[2.75rem] font-light text-[#121317] leading-none mb-2">{s.value}</div>
                                    <div className="text-[0.875rem] text-[#5f6368]">{s.label}</div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                <div>
                    {/* Categories Grid - Show when no language selected */}
                    {!selectedLanguage && (
                        <section className="max-w-7xl mx-auto px-6 pt-12 pb-12">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
                                <div className="max-w-2xl">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#e8f0fe] text-[#1a73e8]">
                                            <GraduationCap size={18} strokeWidth={1.5} />
                                        </div>
                                        <span className="text-[0.8rem] font-semibold uppercase tracking-widest text-[#1a73e8]">Programming Courses</span>
                                    </div>
                                    <h2 className="text-3xl md:text-4xl font-light text-[#121317] mb-3">Choose your language</h2>
                                    <p className="text-[#5f6368] text-lg leading-relaxed">Progressive paths from foundations to advanced topics, tested against current runtime versions.</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {tutorialCategories.map((cat: TutorialCategory, index: number) => (
                                    <m.div
                                        key={cat.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: "-50px" }}
                                        transition={{ duration: 0.6, delay: (index % 2) * 0.1, ease: [0.21, 0.45, 0.32, 0.9] }}
                                    >
                                        <Link
                                            to={`/insights/courses/${cat.id}`}
                                            className="flex flex-row gap-6 p-6 bg-white border border-[#e8eaed] rounded-[32px] hover:shadow-lg transition-all duration-300 no-underline group"
                                        >
                                            <div className="w-[100px] sm:w-[140px] aspect-square rounded-2xl overflow-hidden bg-[#f8f9fa] shrink-0 self-center">
                                                {cat.imageUrl && (
                                                    <OptimizedImage
                                                        src={typeof cat.imageUrl === 'object' ? (cat.imageUrl[600] || cat.imageUrl[400]) : cat.imageUrl}
                                                        srcSet={typeof cat.imageUrl === 'object' ?
                                                            `${cat.imageUrl[200]} 200w, ${cat.imageUrl[300]} 300w, ${cat.imageUrl[400]} 400w, ${cat.imageUrl[600]} 600w` : undefined}
                                                        placeholder={typeof cat.imageUrl === 'object' ? cat.imageUrl.placeholder : undefined}
                                                        alt={cat.name}
                                                        width={190}
                                                        height={190}
                                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                        loading={index < 2 ? 'eager' : 'lazy'}
                                                        priority={index < 2}
                                                        cinematic
                                                    />
                                                )}
                                            </div>
                                            <div className="flex flex-col justify-between flex-1">
                                                <div>
                                                    <span className="text-[0.75rem] font-bold text-[#1a73e8] uppercase tracking-wider mb-2 block">{cat.name}</span>
                                                    <h3 className="text-xl font-medium text-[#121317] mb-2 group-hover:text-[#1a73e8] transition-colors">{cat.name} Engineering Path</h3>
                                                    <p className="text-[0.9rem] text-[#5f6368] leading-relaxed line-clamp-2">{cat.description}</p>
                                                </div>
                                                <span className="inline-flex items-center gap-2 mt-4 px-5 py-2 rounded-full font-medium text-white text-[0.875rem] transition-all hover:opacity-90 bg-[#1a73e8] w-fit no-underline">
                                                    Start learning <ArrowRight size={14} />
                                                </span>
                                            </div>
                                        </Link>
                                    </m.div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Courses List - Show when language selected */}
                    {selectedLanguage && (
                        <section className="max-w-7xl mx-auto px-6 pt-8 pb-12">
                            <div className="flex justify-between items-baseline mb-8 pb-4 border-b border-[#e8eaed]">
                                <h2 className="text-[1.5rem] font-normal text-[#202124]">
                                    {`${currentCategory?.name} Courses`}
                                </h2>
                                <Link
                                    to="/insights/courses"
                                    className="flex items-center gap-2 text-[#5f6368] hover:text-[#1a73e8] text-[0.9375rem] transition-colors duration-200 no-underline"
                                >
                                    <ArrowLeft size={16} /> Back to Hub
                                </Link>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {filteredTutorials.map((tutorial: Course, index: number) => (
                                    <m.div
                                        key={tutorial.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: "-50px" }}
                                        transition={{ duration: 0.6, delay: (index % 2) * 0.1, ease: [0.21, 0.45, 0.32, 0.9] }}
                                    >
                                        <Link
                                            to={`/insights/courses/${tutorial.language}/${tutorial.slug}`}
                                            className="flex flex-row gap-6 p-6 bg-white border border-[#e8eaed] rounded-[32px] hover:shadow-lg transition-all duration-300 no-underline group h-full"
                                        >
                                            <div className="w-[100px] sm:w-[140px] aspect-square rounded-2xl overflow-hidden bg-[#f8f9fa] shrink-0 self-center">
                                                <OptimizedImage
                                                    src={tutorial.imageUrl}
                                                    alt={tutorial.title}
                                                    width={190}
                                                    height={190}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                    loading={index < 2 ? "eager" : "lazy"}
                                                    priority={index < 2}
                                                    cinematic
                                                />
                                            </div>
                                            <div className="flex flex-col justify-between flex-1">
                                                <div>
                                                    <span className="text-[0.75rem] font-bold text-[#1a73e8] uppercase tracking-wider mb-2 block">
                                                        {tutorial.language} Engineering
                                                    </span>
                                                    <h3 className="text-xl font-medium text-[#121317] mb-2 group-hover:text-[#1a73e8] transition-colors leading-snug">
                                                        {tutorial.title}
                                                    </h3>
                                                    <p className="text-[0.9rem] text-[#5f6368] leading-relaxed line-clamp-2">
                                                        {tutorial.excerpt}
                                                    </p>
                                                </div>
                                                <span className="inline-flex items-center gap-2 mt-4 px-5 py-2 rounded-full font-medium text-white text-[0.875rem] transition-all hover:opacity-90 bg-[#1a73e8] w-fit no-underline">
                                                    Start learning <ArrowRight size={14} />
                                                </span>
                                            </div>
                                        </Link>
                                    </m.div>
                                ))}
                            </div>
                        </section>
                    )}


                </div>
            </div>
        </>
    );
};

export default CoursesHub;
